import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaPrint, FaChevronUp } from "react-icons/fa";

function PrivacyPolicyPage() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePrint = () => {
    window.print();
  };

  const sections = [
    { id: "information-collect", title: "1. Information We Collect" },
    { id: "how-we-use", title: "2. How We Use Your Information" },
    { id: "who-we-share", title: "3. Who We Share Data With" },
    { id: "privacy-rights", title: "4. Your Privacy Rights and Controls" },
    { id: "data-security", title: "5. Data Security" },
    { id: "data-retention", title: "6. Data Retention" },
    { id: "international-transfers", title: "7. International Data Transfers" },
    { id: "childrens-privacy", title: "8. Children's Privacy" },
    { id: "california-rights", title: "9. California Privacy Rights" },
    { id: "changes-policy", title: "10. Changes to This Policy" },
    { id: "contact-us", title: "11. Contact Us" },
    { id: "gmail-disclosure", title: "Gmail API Disclosure" },
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy - OmniButler</title>
        <style type="text/css">
          {`
            @media print {
              .no-print {
                display: none !important;
              }
              .print-break-inside-avoid {
                break-inside: avoid;
              }
              body {
                font-size: 12pt;
              }
              .container {
                max-width: 100% !important;
              }
            }
          `}
        </style>
      </Helmet>

      <main className="mt-14">
        {/* Back to Home Link and Print Button */}
        <div className="container py-6 no-print">
          <div className="flex justify-between items-center">
            <Link
              to="/"
              className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium transition-colors duration-300 group"
            >
              <FaArrowLeft className="mr-2 text-sm transition-transform duration-300 group-hover:-translate-x-1" />
              Back to Home
            </Link>
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-300"
            >
              <FaPrint className="mr-2" />
              Print Policy
            </button>
          </div>
        </div>

        <section className="container py-16">
          <div className="max-w-4xl mx-auto">
            {/* Header with prominent last updated */}
            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold mb-6">
                Privacy Policy - OmniButler
              </h1>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-200 dark:border-indigo-800 rounded-lg p-4 inline-block">
                <p className="text-xl font-semibold text-indigo-700 dark:text-indigo-300">
                  Last Updated: July 24, 2025
                </p>
              </div>
            </div>

            {/* Table of Contents */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-12 no-print">
              <h2 className="text-xl font-semibold mb-4">Quick Navigation</h2>
              <div className="grid md:grid-cols-2 gap-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors duration-300"
                  >
                    {section.title}
                  </a>
                ))}
              </div>
            </div>

            {/* We Take Your Privacy Seriously */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 mb-12 print-break-inside-avoid">
              <h2 className="text-3xl font-bold mb-6">We Take Your Privacy Seriously</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                At OmniButler, protecting your privacy isn&apos;t just a legal requirement—it&apos;s fundamental to earning your trust. This Privacy Policy explains how we collect, use, protect, and respect your personal and financial information.
              </p>
              <div className="bg-white dark:bg-gray-700 rounded-lg p-6">
                <p className="font-semibold mb-4">The Bottom Line:</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✅</span>
                    <span className="text-gray-700 dark:text-gray-300">You own your data</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✅</span>
                    <span className="text-gray-700 dark:text-gray-300">We never sell your personal information</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✅</span>
                    <span className="text-gray-700 dark:text-gray-300">We never see or store your bank passwords</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✅</span>
                    <span className="text-gray-700 dark:text-gray-300">Your data is physically isolated in its own database</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2">✅</span>
                    <span className="text-gray-700 dark:text-gray-300">You can delete your account and data anytime</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Privacy Policy Content */}
            <div className="space-y-12">
              {/* Section 1 */}
              <div id="information-collect" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  1. Information We Collect
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">1.1 Account Information</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-2">
                      <strong>What we collect</strong>: Name, email address, and Google account identifier
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 mb-2">
                      <strong>How we collect it</strong>: Through Google OAuth when you sign up
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">
                      <strong>What we DON&apos;T collect</strong>: Passwords (we use Google sign-in exclusively)
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">1.2 Financial Information</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-2">
                      <strong>What we collect</strong>: Account balances, transaction history, and account details
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 mb-2">
                      <strong>How we collect it</strong>: Through our secure partner Plaid
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      <strong>Important</strong>: We NEVER see or store your bank login credentials. When you connect a bank account:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700 dark:text-gray-300">
                      <li>You log in directly with your bank through Plaid&apos;s secure interface</li>
                      <li>Plaid handles the authentication with bank-level security</li>
                      <li>We only receive read-only access to your financial data</li>
                      <li>You can disconnect accounts anytime</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">1.3 Communication Data</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      <strong>Email</strong> (via Gmail integration):
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700 dark:text-gray-300 mb-4">
                      <li>Read-only access to scan for bills, receipts, and financial documents</li>
                      <li>We process emails locally and don&apos;t store email passwords</li>
                    </ul>
                    
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      <strong>WhatsApp</strong> (optional):
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700 dark:text-gray-300 mb-4">
                      <li>Phone number and message content</li>
                      <li>Location data (only when you share it)</li>
                      <li>Images and voice messages (only when you send them)</li>
                    </ul>
                    
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      <strong>Calendar</strong> (via Google Calendar):
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700 dark:text-gray-300">
                      <li>Event details to help with scheduling and reminders</li>
                      <li>We can create events based on your requests</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">1.4 Usage Information</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      <li>Features you use and how often</li>
                      <li>Device information (browser, IP address)</li>
                      <li>Performance metrics to improve our service</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 2 */}
              <div id="how-we-use" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  2. How We Use Your Information
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">2.1 To Provide Our Core Services</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li><strong>Financial Overview</strong>: Aggregate your accounts in one secure dashboard</li>
                      <li><strong>AI Assistance</strong>: Answer questions about your finances using advanced AI</li>
                      <li><strong>Smart Search</strong>: Find transactions, emails, and documents instantly</li>
                      <li><strong>Family Sharing</strong>: Share selected financial data with family members</li>
                      <li><strong>Notifications</strong>: Alert you about important financial events</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">2.2 How Our AI Works</h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      We use leading AI providers (OpenAI, Anthropic, Together AI) to:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                      <li>Generate intelligent responses to your questions</li>
                      <li>Categorize transactions and identify patterns</li>
                      <li>Extract insights from financial documents</li>
                      <li>Create summaries of your financial situation</li>
                    </ul>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4">
                      <p className="text-gray-700 dark:text-gray-300">
                        <strong>Important</strong>: Our AI provides information and insights only. It may occasionally be inaccurate. Always verify important financial decisions with qualified professionals.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">2.3 What We DON&apos;T Do</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="text-red-500 mr-2">❌</span>
                        <span className="text-gray-700 dark:text-gray-300">Sell your data to anyone</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-2">❌</span>
                        <span className="text-gray-700 dark:text-gray-300">Share your data with advertisers</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-2">❌</span>
                        <span className="text-gray-700 dark:text-gray-300">Use your data to train general AI models</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-red-500 mr-2">❌</span>
                        <span className="text-gray-700 dark:text-gray-300">Make financial decisions for you</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 3 */}
              <div id="who-we-share" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  3. Who We Share Data With
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">3.1 Service Providers (Your Data Never Leaves These Trusted Partners)</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      <li><strong>Plaid</strong>: Secure bank connections</li>
                      <li><strong>Firebase/Google Cloud</strong>: Infrastructure and data storage</li>
                      <li><strong>OpenAI/Anthropic</strong>: AI processing (with strict data agreements)</li>
                      <li><strong>Twilio</strong>: WhatsApp messaging</li>
                      <li><strong>Redis</strong>: Temporary data caching</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">3.2 Family Sharing (You Control Everything)</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      <li>Only family members you explicitly invite</li>
                      <li>You choose which accounts to share</li>
                      <li>Each member has their own login</li>
                      <li>You can revoke access anytime</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">3.3 Legal Requirements</h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      We may disclose information if required by law, but we&apos;ll notify you unless legally prohibited.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 4 */}
              <div id="privacy-rights" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  4. Your Privacy Rights and Controls
                </h2>
                
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  You have complete control over your data:
                </p>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">4.1 Access Your Data</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      <li>Download all your data anytime from settings</li>
                      <li>See exactly what information we have</li>
                      <li>Export in standard formats</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">4.2 Correct Your Data</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      <li>Update incorrect information directly in the app</li>
                      <li>Contact support for additional help</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">4.3 Delete Your Data</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      <li><strong>Account Deletion</strong>: Delete your account from settings</li>
                      <li><strong>Timeline</strong>: We remove your data within 30 days</li>
                      <li><strong>Exceptions</strong>: Some data retained for legal compliance (fraud prevention, tax records)</li>
                      <li><strong>Instant Removal</strong>: Conversation history deleted immediately</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">4.4 Control Connections</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      <li>Disconnect bank accounts instantly</li>
                      <li>Revoke Gmail access anytime</li>
                      <li>Unlink WhatsApp immediately</li>
                      <li>Manage family member access</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 5 */}
              <div id="data-security" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  5. Data Security
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold mb-3">5.1 How We Protect Your Data</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                      <li><strong>Database Isolation</strong>: For maximum security, each user&apos;s emails and transactions are stored in completely separate databases—not just separate rows in a shared database</li>
                      <li><strong>Encryption</strong>: All data encrypted in transit (TLS) and at rest</li>
                      <li><strong>Authentication</strong>: Secure login via Firebase/Google OAuth</li>
                      <li><strong>Access Control</strong>: Strict employee access on need-to-know basis</li>
                      <li><strong>Monitoring</strong>: 24/7 security monitoring and alerts</li>
                      <li><strong>Infrastructure</strong>: Google Cloud security with 99.95% uptime SLA</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-3">5.2 Your Security Responsibilities</h3>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      <li>Keep your Google account secure</li>
                      <li>Use strong passwords and 2FA</li>
                      <li>Don&apos;t share login credentials</li>
                      <li>Report suspicious activity immediately</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 6 */}
              <div id="data-retention" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  6. Data Retention
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We keep your data only as long as necessary:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                  <li><strong>Active Accounts</strong>: Data retained while you use our service</li>
                  <li><strong>Deleted Accounts</strong>: Personal data removed within 30 days</li>
                  <li><strong>Conversation History</strong>: Auto-expires based on your settings</li>
                  <li><strong>Legal Requirements</strong>: Some data kept longer if required by law</li>
                  <li><strong>Aggregated Data</strong>: Anonymous analytics kept indefinitely</li>
                </ul>
              </div>

              {/* Section 7 */}
              <div id="international-transfers" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  7. International Data Transfers
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  Your data is stored and processed in:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                  <li>European Union (primary data storage and hosting)</li>
                  <li>We intentionally host in the EU to ensure strong privacy law protections</li>
                  <li>Some third-party services (AI providers) may process data in other jurisdictions</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  We ensure all transfers comply with applicable data protection laws.
                </p>
              </div>

              {/* Section 8 */}
              <div id="childrens-privacy" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  8. Children&apos;s Privacy
                </h2>
                <p className="text-gray-700 dark:text-gray-300">
                  OmniButler is not for users under 18. We don&apos;t knowingly collect data from children. If we discover we have, we&apos;ll delete it immediately.
                </p>
              </div>

              {/* Section 9 */}
              <div id="california-rights" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  9. California Privacy Rights
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  California residents have additional rights under CCPA:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Right to know what personal information we collect</li>
                  <li>Right to delete personal information</li>
                  <li>Right to opt-out of &quot;sales&quot; (we don&apos;t sell data)</li>
                  <li>Right to non-discrimination</li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300">
                  To exercise these rights:{" "}
                  <a
                    href="mailto:privacy@omnibutler.com"
                    className="text-indigo-600 hover:text-indigo-700"
                  >
                    privacy@omnibutler.com
                  </a>
                </p>
              </div>

              {/* Section 10 */}
              <div id="changes-policy" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  10. Changes to This Policy
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  We&apos;ll notify you of significant changes via:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Email to your registered address</li>
                  <li>In-app notification</li>
                  <li>30 days before changes take effect</li>
                </ul>
              </div>

              {/* Section 11 */}
              <div id="contact-us" className="scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-3xl font-bold mb-6">
                  11. Contact Us
                </h2>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <p><strong>Privacy Questions</strong>: <a href="mailto:privacy@omnibutler.com" className="text-indigo-600 hover:text-indigo-700">privacy@omnibutler.com</a></p>
                  <p><strong>General Support</strong>: <a href="mailto:support@omnibutler.com" className="text-indigo-600 hover:text-indigo-700">support@omnibutler.com</a></p>
                  <p><strong>Data Protection Officer</strong>: <a href="mailto:dpo@omnibutler.com" className="text-indigo-600 hover:text-indigo-700">dpo@omnibutler.com</a></p>
                </div>
              </div>

              {/* Gmail API Disclosure */}
              <div id="gmail-disclosure" className="border-t pt-8 scroll-mt-20 print-break-inside-avoid">
                <h2 className="text-2xl font-bold mb-4">
                  Gmail API Disclosure
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  OmniButler&apos;s use of information received from Gmail APIs will adhere to{" "}
                  <a
                    href="https://developers.google.com/terms/api-services-user-data-policy"
                    className="text-indigo-600 hover:text-indigo-700"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Google API Services User Data Policy
                  </a>
                  , including the Limited Use requirements.
                </p>
                
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  <strong>We use Gmail data solely to</strong>:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 mb-4">
                  <li>Identify bills and financial documents</li>
                  <li>Extract transaction information</li>
                  <li>Provide search functionality</li>
                  <li>Generate financial insights</li>
                </ul>
                
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  <strong>We never</strong>:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                  <li>Share email content with third parties</li>
                  <li>Use emails for advertising</li>
                </ul>
              </div>

              {/* Footer note */}
              <div className="border-t pt-8 text-sm text-gray-600 dark:text-gray-400 italic">
                <p>
                  This privacy policy is effective as of July 24, 2025 and replaces all previous versions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Scroll to top button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 no-print"
            aria-label="Scroll to top"
          >
            <FaChevronUp className="w-5 h-5" />
          </button>
        )}
      </main>
    </>
  );
}

export default PrivacyPolicyPage;